type AccountSuspensionState = {
  status?: unknown;
  account_status?: unknown;
  suspended?: unknown;
  suspended_reason?: unknown;
  suspensionHistory?: {
    event_type?: unknown;
    eventType?: unknown;
    notes?: unknown;
    reason?: unknown;
    created_at?: unknown;
    createdAt?: unknown;
    performed_by?: unknown;
    performedBy?: unknown;
  } | null;
};

export type AccountSuspensionDetails = {
  reason: string | null;
  createdAt: string | null;
  performedBy: string | null;
  isRica: boolean;
};

const nonEmptyText = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

/** Read both Nova account_history and the legacy customer-history contract. */
export function getAccountSuspension(
  account?: AccountSuspensionState | null,
): AccountSuspensionDetails | null {
  if (!account || !isAccountSuspended(account)) return null;
  const history = account.suspensionHistory;
  const event = nonEmptyText(
    history?.event_type,
    history?.eventType,
  )?.toUpperCase();
  const currentHistory =
    !event || event === 'SUSPENDED' || event === 'ACCOUNT_SUSPENDED'
      ? history
      : null;
  const reason = nonEmptyText(
    currentHistory?.notes,
    currentHistory?.reason,
    // An explicit restoration event must not revive a stale legacy reason.
    event && !currentHistory ? null : account.suspended_reason,
  );
  const timestamp = nonEmptyText(
    currentHistory?.created_at,
    currentHistory?.createdAt,
  );
  return {
    reason,
    createdAt:
      timestamp && Number.isFinite(Date.parse(timestamp)) ? timestamp : null,
    performedBy: nonEmptyText(
      currentHistory?.performed_by,
      currentHistory?.performedBy,
    ),
    // Display classification only; the server still decides resend eligibility.
    isRica:
      /^(rica|rica suspended|rica verification overdue after 4 days\.?)$/i.test(
        reason ?? '',
      ),
  };
}

export const isAccountSuspended = (
  account?: AccountSuspensionState | null,
): boolean => {
  if (!account) return false;

  const status = String(account.status ?? account.account_status ?? '')
    .trim()
    .toUpperCase();

  if (status) {
    return status === 'SUSPENDED';
  }

  const legacySuspended = account.suspended;
  return (
    legacySuspended === true ||
    legacySuspended === 1 ||
    String(legacySuspended ?? '')
      .trim()
      .toLowerCase() === 'true'
  );
};
